/**
 * Connection Status Component
 * Shows current connection state with appropriate styling and actions
 */

import React from 'react';
import { ConversationState } from '../../types/conversation';
import styles from './ConnectionStatus.module.css';

type ConnectionStatusType = ConversationState['connectionStatus'];

interface ConnectionStatusProps {
  status: ConnectionStatusType;
  error?: string | null;
  message?: string;
  onRetry?: () => void;
  isRetrying?: boolean;
  attemptCount?: number;
  nextRetryIn?: number;
  canRetry?: boolean;
  className?: string;
  showWhenConnected?: boolean;
}

export const ConnectionStatusComponent: React.FC<ConnectionStatusProps> = ({
  status,
  error,
  message,
  onRetry,
  isRetrying = false,
  attemptCount = 0,
  nextRetryIn = 0,
  canRetry = true,
  className = '',
  showWhenConnected = false,
}) => {
  // Don't show when connected unless explicitly requested
  if (status === 'connected' && !showWhenConnected) {
    return null;
  }

  const getStatusIcon = () => {
    switch (status) {
      case 'connected':
        return '✅';
      case 'connecting':
        return '🔄';
      case 'disconnected':
        return '❌';
      default:
        return '⚠️';
    }
  };

  const getStatusClass = () => {
    switch (status) {
      case 'connected':
        return styles.connected;
      case 'connecting':
        return styles.connecting;
      case 'disconnected':
        return styles.disconnected;
      default:
        return styles.error;
    }
  };

  const shouldShowRetryButton = () => {
    return status === 'disconnected' && 
           onRetry && 
           canRetry && 
           !isRetrying;
  };

  const getDisplayMessage = () => {
    if (message) return message;
    
    if (isRetrying && nextRetryIn > 0) {
      return `Retrying in ${nextRetryIn}s...`;
    }
    
    if (isRetrying) {
      return 'Retrying...';
    }
    
    if (attemptCount > 0 && status === 'disconnected') {
      return `Connection failed (${attemptCount} attempts)`;
    }
    
    switch (status) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      case 'disconnected':
        return error || 'Connection lost';
      default:
        return 'Unknown status';
    }
  };

  return (
    <div className={`${styles.container} ${getStatusClass()} ${className}`}>
      <div className={styles.content}>
        <span className={styles.icon} aria-hidden="true">
          {getStatusIcon()}
        </span>
        
        <span className={styles.message} role="status" aria-live="polite">
          {getDisplayMessage()}
        </span>
        
        {shouldShowRetryButton() && (
          <button
            className={styles.retryButton}
            onClick={onRetry}
            disabled={isRetrying || !canRetry}
            aria-label="Retry connection"
          >
            Retry
          </button>
        )}
      </div>
      
      {status === 'connecting' && (
        <div className={styles.progressBar}>
          <div className={styles.progressFill}></div>
        </div>
      )}
    </div>
  );
};

export default ConnectionStatusComponent;
